import { Link, NavLink } from "react-router-dom";
import { Instagram, Linkedin } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { WaitlistButton } from "@/components/Waitlist";

export const IMG = {
  lindsay: "/brand/lindsay-fenn.jpg",
  event1: "/brand/eg-event-1.webp",
  event2: "/brand/eg-event-2.webp",
  own1: "/brand/photo-a.webp",
  own2: "/brand/photo-b.webp",
  bottles: "https://images.unsplash.com/photo-1520090377527-6090c8a99dad?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
  cafe: "https://images.pexels.com/photos/16503157/pexels-photo-16503157.jpeg?auto=compress&cs=tinysrgb&w=1200",
  market: "https://images.unsplash.com/photo-1604200657090-ae45994b2451?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200",
};
// Stock images: Unsplash/Pexels licences (free commercial use). /brand/* photos supplied by Lindsay.
export const SOCIAL = {
  instagram: "https://www.instagram.com/roots_marketing_co_uk/",
  linkedin: "https://www.linkedin.com/in/lindsay-castling-fenn",
};

const NavAuth = () => {
  const { user } = useAuth();
  if (user) return <Link to={user.role === "admin" ? "/admin" : "/app"} data-testid="nav-dashboard" className="rounded-full bg-navy hover:bg-navy/90 text-white px-3 sm:px-4 py-2 whitespace-nowrap transition-colors">My dashboard</Link>;
  return (
    <>
      <Link to="/login" data-testid="nav-login" className="whitespace-nowrap px-2 py-1 hover:text-pinkb transition-colors">Log in</Link>
      <WaitlistButton testId="nav-join" arrow={false} className="px-3 sm:px-4 py-2 whitespace-nowrap text-sm" />
    </>
  );
};

export const SiteNav = () => (
  <header className="relative z-20 border-b border-border/60 bg-background/80 backdrop-blur-md sticky top-0">
    <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
      <Link to="/" className="flex items-center" data-testid="site-logo"><img src="/brand/logo.png" alt="Roots Marketing Co" className="h-9 sm:h-11 w-auto" /></Link>
      <nav className="flex items-center gap-1 sm:gap-4 text-sm">
        <NavLink to="/the-roots-club" data-testid="nav-club" className="hidden sm:inline px-2 py-1 hover:text-pinkb transition-colors">The Roots Club</NavLink>
        <NavLink to="/mini-marketing-plan" data-testid="nav-mini-plan" className="hidden md:inline px-2 py-1 hover:text-pinkb transition-colors">Mini Marketing Plan</NavLink>
        <NavAuth />
      </nav>
    </div>
  </header>
);

export const SiteFooter = ({ email }) => (
  <footer className="relative z-10 bg-navy text-white/80 mt-24" data-testid="site-footer">
    <div className="mx-auto max-w-7xl px-6 lg:px-10 py-14 grid gap-10 md:grid-cols-3">
      <div>
        <img src="/brand/logo.png" alt="Roots Marketing Co" className="h-12 w-auto rounded-lg bg-white p-2" />
        <p className="mt-3 text-sm max-w-xs">Foundations-first marketing for independent food, drink and hospitality founders.</p>
      </div>
      <div className="text-sm space-y-2">
        <Link to="/the-roots-club" className="block hover:text-sun">The Roots Club</Link>
        <Link to="/mini-marketing-plan" data-testid="footer-mini-plan" className="block hover:text-sun">Mini Marketing Plan</Link>
        {email && <a href={`mailto:${email}`} data-testid="footer-email" className="block hover:text-sun">{email}</a>}
        <div className="flex gap-3 pt-2">
          <a href={SOCIAL.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-instagram" className="hover:text-sun"><Instagram className="h-5 w-5" /></a>
          <a href={SOCIAL.linkedin} target="_blank" rel="noreferrer" aria-label="LinkedIn" data-testid="footer-linkedin" className="hover:text-sun"><Linkedin className="h-5 w-5" /></a>
        </div>
      </div>
      <div className="text-sm space-y-2">
        <Link to="/terms" data-testid="footer-terms" className="block hover:text-sun">Terms and Conditions</Link>
        <Link to="/privacy" data-testid="footer-privacy" className="block hover:text-sun">Privacy Policy</Link>
        <Link to="/cookies" data-testid="footer-cookies" className="block hover:text-sun">Cookie Policy</Link>
      </div>
    </div>
    <div className="border-t border-white/10 py-5 text-center text-xs text-white/50">© {new Date().getFullYear()} Roots Marketing Co · Lindsay Fenn</div>
  </footer>
);

export const ProofRoll = ({ items }) => {
  if (!items?.length) return null;
  if (items.length <= 3) return (
    <div className="mx-auto max-w-7xl px-6 lg:px-10 grid gap-5 md:grid-cols-3" data-testid="social-proof-grid">
      {items.map((t, i) => (
        <figure key={i} className="rounded-2xl border border-border bg-card p-6 flex flex-col justify-between" data-testid={`testimonial-card-${i}`}>
          {t.rating && <div className="text-sun text-sm tracking-widest">{"★".repeat(t.rating)}</div>}
          <blockquote className="text-sm leading-relaxed text-navy">“{t.quote}”</blockquote>
          <figcaption className="mt-4 text-xs text-muted-foreground">{t.name}{t.business && ` · ${t.business}`}</figcaption>
        </figure>
      ))}
    </div>
  );
  const loop = [...items, ...items];
  return (
    <div className="relative overflow-hidden py-4 [mask-image:linear-gradient(90deg,transparent,black_8%,black_92%,transparent)]" data-testid="social-proof-roll">
      <div className="flex w-max gap-5 animate-[marquee_60s_linear_infinite] hover:[animation-play-state:paused]">
        {loop.map((t, i) => t.placeholder ? (
          <figure key={i} className="w-80 shrink-0 rounded-2xl border-2 border-dashed border-border bg-card p-6 flex flex-col justify-between" data-testid={i < items.length ? `testimonial-placeholder-${t.key}` : undefined}>
            <div className="text-sun/50 text-sm tracking-widest">★★★★★</div>
            <p className="mt-2 text-sm italic text-muted-foreground">Your review could be here. Member stories are coming soon.</p>
            <div className="mt-4 h-3 w-24 rounded bg-secondary" />
          </figure>
        ) : (
          <figure key={i} className="w-80 shrink-0 rounded-2xl border border-border bg-card p-6">
            {t.rating && <div className="text-sun text-sm tracking-widest">{"★".repeat(t.rating)}</div>}
            <blockquote className="mt-2 text-sm leading-relaxed text-navy">“{t.quote}”</blockquote>
            <figcaption className="mt-4 text-xs text-muted-foreground">{t.name}{t.business && ` · ${t.business}`}{t.kind === "member" && <span className="ml-2 rounded-full bg-leaf/15 text-leaf px-2 py-0.5 text-[10px] uppercase">Member</span>}</figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
};

export const ClientRoll = ({ clients }) => clients?.length > 0 && (
  <div className="relative overflow-hidden py-6 border-y border-border [mask-image:linear-gradient(90deg,transparent,black_8%,black_92%,transparent)]" data-testid="client-roll">
    <div className="flex w-max gap-12 animate-[marquee_45s_linear_infinite]">
      {[...clients, ...clients].map((c, i) => <span key={i} className="font-serif text-xl uppercase text-navy/40 whitespace-nowrap">{c}</span>)}
    </div>
  </div>
);
